<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import PaymentsTable from '../components/PaymentsTable.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const payments = useMapGetter('payments/getPaymentsList');
const uiFlags = useMapGetter('payments/getUIFlags');
const meta = useMapGetter('payments/getMeta');

const pageNumber = computed(() => Number(route.query?.page) || 1);
const currentPage = computed(() => Number(meta.value?.currentPage) || 1);
const totalItems = computed(() => meta.value?.count || 0);
const isFetching = computed(() => uiFlags.value.isFetching);
const hasPayments = computed(() => payments.value?.length > 0);

const toDateInputValue = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Inclusive window: today and the 59 days before it.
const defaultDateFrom = () => {
  const date = new Date();
  date.setDate(date.getDate() - 59);
  return toDateInputValue(date);
};

const defaultDateTo = () => toDateInputValue(new Date());

const dateFrom = ref(route.query.date_from || defaultDateFrom());
const dateTo = ref(route.query.date_to || defaultDateTo());
const requestStatus = ref(route.query.request_status || '');
const paymentStatus = ref(route.query.payment_status || '');

const updatePageParam = (page, filters = {}) => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...filters,
  };
  
  // Remove empty filter params
  Object.keys(query).forEach(key => {
    if (query[key] === '' || query[key] === null || query[key] === undefined) {
      delete query[key];
    }
  });
  
  router.replace({ query });
};

const fetchPayments = async (page = 1) => {
  const filters = {
    dateFrom: dateFrom.value || null,
    dateTo: dateTo.value || null,
    requestStatus: requestStatus.value || null,
    paymentStatus: paymentStatus.value || null,
  };
  
  await store.dispatch('payments/get', {
    page,
    sortAttr: '-created_at',
    clientId: null,
    ...filters,
  });
  
  updatePageParam(page, {
    date_from: dateFrom.value,
    date_to: dateTo.value,
    request_status: requestStatus.value,
    payment_status: paymentStatus.value,
  });
};

const handleFilterChange = async () => {
  await fetchPayments(1);
};

const handlePageChange = async page => {
  await fetchPayments(page);
};

const handlePaymentUpdated = async () => {
  await fetchPayments(currentPage.value);
};

onMounted(async () => {
  // Initialize filters from query params, otherwise last 60 days including today
  dateFrom.value = route.query.date_from || defaultDateFrom();
  dateTo.value = route.query.date_to || defaultDateTo();
  requestStatus.value = route.query.request_status || '';
  paymentStatus.value = route.query.payment_status || '';
  
  await fetchPayments(pageNumber.value);
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background p-6"
  >
    <div class="mb-4">
      <h1 class="text-2xl font-semibold text-n-slate-12 mb-4">Payments</h1>
      
      <!-- Filters -->
      <div class="flex flex-wrap gap-4 mb-4 p-4 bg-n-slate-2 rounded-lg border border-n-slate-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">Date From</label>
          <Input
            v-model="dateFrom"
            type="date"
            placeholder="Select start date"
            @change="handleFilterChange"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">Date To</label>
          <Input
            v-model="dateTo"
            type="date"
            placeholder="Select end date"
            @change="handleFilterChange"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">Request Status</label>
          <select
            v-model="requestStatus"
            class="h-10 px-3 py-2 border border-n-slate-6 rounded-md bg-n-slate-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent ltr:pr-8 rtl:pl-8"
            @change="handleFilterChange"
          >
            <option value="">All</option>
            <option value="0">Open</option>
            <option value="1">Resolved</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">Payment Status</label>
          <select
            v-model="paymentStatus"
            class="h-10 px-3 py-2 border border-n-slate-6 rounded-md bg-n-slate-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent ltr:pr-8 rtl:pl-8"
            @change="handleFilterChange"
          >
            <option value="">All</option>
            <option value="0">Open</option>
            <option value="1">Paid</option>
            <option value="2">Expired</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11 opacity-0">Clear</label>
          <button
            @click="() => {
              dateFrom = '';
              dateTo = '';
              requestStatus = '';
              paymentStatus = '';
              handleFilterChange();
            }"
            class="h-10 px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-slate-3 border border-n-slate-6 rounded-md hover:bg-n-slate-4"
          >
            Clear Filters
          </button>
        </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11 opacity-0">Refresh</label>
        <button
          @click="handlePaymentUpdated"
          class="h-10 px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-brand border border-n-brand rounded-md hover:bg-n-brand-dark"
        >
          Refresh
        </button>
      </div>
      </div>
    </div>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <div v-else-if="!hasPayments" class="flex items-center justify-center py-10">
      <span class="text-base text-n-slate-11">No payments found</span>
    </div>

    <PaymentsTable
      v-else
      :payments="payments"
      :current-page="currentPage"
      :total-items="totalItems"
      @page-change="handlePageChange"
      @payment-updated="handlePaymentUpdated"
    />
  </div>
</template>
